import type {WizardData} from '../index'
import {classIdentities, classIdentity, levelIdentity} from './academicStructure'
import {
  type CurriculumFields,
  buildLevelCurriculumLookup,
  curriculumFieldsFor,
  lookupLevelId,
  lookupSubjectId,
  parseLevelIdKey,
} from './levelCurriculum'

type ManualData = WizardData['manualData']
type Loose = Record<string, any>

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown) => trim(value).toLowerCase()
const same = (a: unknown, b: unknown) => lower(a) === lower(b)
/** Equal, or either side unknown (legacy rows carry no branch / curriculum). */
const sameOrUnknown = (a: unknown, b: unknown) => !trim(a) || !trim(b) || same(a, b)

/**
 * Deletes made on Academic Structure are a pending command queue (`deletedClasses`,
 * `deletedLevels`): they only reach the backend on that step's Next. Two things follow.
 *
 * 1. A logout before Next must not lose them, so they are persisted with the classes draft and
 *    restored on resume. But GET /onboarding/data still returns everything they name, and the
 *    resume mapper would paint it straight back — the level with no subjects, the class the
 *    user removed. `applyPendingDeletes` re-applies the queue to the hydrated data so the page
 *    matches what the user last saw, and keeps the queue for the next sync.
 * 2. The bulk-import branch payload only lists the levels that still exist, which leaves a
 *    removed level in the tenant DB. `buildLevelDeletePayload` produces the branch entries that
 *    delete it, sent as a SEPARATE request after the classes request: classes resolve
 *    server-side by name + level, so the level must outlive its classes' delete.
 *
 * Branches removed on Institution Setup (`deletedBranches`) and subjects removed from a level
 * (`deletedSubjects`) are queued the same way. A branch delete is its own request after the level
 * one (node-api drops the whole branch with it); subject deletes ride the subjects section.
 */

const listOf = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

/**
 * Remove a deselected curriculum and its dependent draft data. Saved classes are queued for
 * deletion so deselection cannot leave them behind on the next sync.
 */
export const pruneDeselectedCurriculumData = (
  manualData: ManualData,
  selectedCurriculums: readonly string[]
): ManualData => {
  const selected = new Set(selectedCurriculums.map(lower))
  const curriculumLookup = buildLevelCurriculumLookup(manualData)
  const curriculumFor = (row: Loose, branch = row?.branch) =>
    curriculumLookup.resolve(branch, row?.level, row?.curriculum)
  const deselectedLevels: NonNullable<ManualData['deletedLevels']> = []
  const addDeselectedLevel = (curriculum: unknown, name: unknown, branch?: unknown) => {
    const levelName = trim(name)
    const curriculumName = trim(curriculum)
    if (!levelName || !curriculumName || selected.has(lower(curriculumName))) return
    if (
      deselectedLevels.some(
        (entry) =>
          same(entry.curriculum, curriculumName) &&
          same(entry.name, levelName) &&
          same(entry.branch, branch)
      )
    ) {
      return
    }
    deselectedLevels.push({
      curriculum: curriculumName,
      name: levelName,
      ...(trim(branch) ? {branch: trim(branch)} : {}),
    })
  }
  Object.entries(manualData.levelsByCurriculum ?? {}).forEach(([curriculum, config]) => {
    if (selected.has(lower(curriculum))) return
    if (config.levelsSameAcrossBranches === false) {
      Object.entries(config.levelsPerBranch ?? {}).forEach(([branch, levels]) =>
        (levels ?? []).forEach((level) => addDeselectedLevel(curriculum, level, branch))
      )
    } else {
      ;(config.levels ?? []).forEach((level) => addDeselectedLevel(curriculum, level.name))
    }
    Object.keys(config.levelDetails ?? {}).forEach((level) =>
      addDeselectedLevel(curriculum, level)
    )
  })
  listOf<Loose>(manualData.branches).forEach((branch) => {
    if (!Array.isArray(branch?.levels)) return
    branch.levels.forEach((level: Loose | string) => {
      const name = typeof level === 'string' ? level : level?.name
      const curriculum =
        typeof level === 'string'
          ? curriculumLookup.resolve(branch.name, name)
          : curriculumLookup.resolve(branch.name, name, level?.curriculum)
      addDeselectedLevel(curriculum, name, branch.name)
    })
  })
  const belongsToDeselectedCurriculum = (row: Loose, branch?: string) => {
    const curriculum = curriculumFor(row, branch)
    return Boolean(curriculum && !selected.has(lower(curriculum)))
  }
  const belongsToSelectedCurriculum = (branch: unknown, level: unknown) =>
    Object.entries(manualData.levelsByCurriculum ?? {}).some(([curriculum, config]) => {
      if (!selected.has(lower(curriculum))) return false
      if (config.levelsSameAcrossBranches === false) {
        return (config.levelsPerBranch?.[String(branch ?? '')] ?? []).some((name) =>
          same(name, level)
        )
      }
      return (config.levels ?? []).some((entry) => same(entry.name, level))
    })

  const previousClasses = listOf<Loose>(manualData.classes)
  const removedClasses = previousClasses.filter((row) => belongsToDeselectedCurriculum(row))

  const deletedClasses = [...listOf<Loose>(manualData.deletedClasses)]
  const queuedClassKeys = new Set(
    deletedClasses.map((row) => classIdentity(row?.name, row?.branch, row?.level, row?.curriculum))
  )
  removedClasses.forEach((row) => {
    if (row?.persisted !== true && !(Number(row?.id) > 0)) return
    const curriculum = curriculumFor(row)
    const identity = classIdentity(row?.name, row?.branch, row?.level, curriculum)
    if (queuedClassKeys.has(identity)) return
    queuedClassKeys.add(identity)
    deletedClasses.push({
      grade: trim(row?.grade) || trim(row?.name).split('-')[0],
      section: trim(row?.section),
      name: trim(row?.name),
      branch: trim(row?.branch) || undefined,
      level: trim(row?.level),
      curriculum: curriculum || undefined,
      ...(Number(row?.id) > 0 ? {id: Number(row.id)} : {}),
      ...(row?.persisted === true ? {persisted: true} : {}),
    })
  })

  const retainedClasses = previousClasses.filter((row) => !belongsToDeselectedCurriculum(row))
  const retainedSubjects = listOf<Loose>(manualData.subjects).filter(
    (row) =>
      !belongsToDeselectedCurriculum(row) &&
      !removedClasses.some(
        (cls) =>
          same(cls?.name, row?.class) &&
          same(cls?.branch, row?.branch) &&
          same(cls?.level, row?.level) &&
          (!trim(row?.curriculum) || same(cls?.curriculum, row?.curriculum))
      )
  )

  const levelsByCurriculum = Object.fromEntries(
    Object.entries(manualData.levelsByCurriculum ?? {}).filter(([curriculum]) =>
      selected.has(lower(curriculum))
    )
  )

  const subjectSelectionsByLevel = Object.fromEntries(
    Object.entries(manualData.subjectSelectionsByLevel ?? {}).filter(([key]) => {
      const curriculum = key.split('|||')[0]
      return selected.has(lower(curriculum))
    })
  )
  const classesReviewRowsByBranch = Object.fromEntries(
    Object.entries(
      ((manualData as Loose).classesReviewRowsByBranch ?? {}) as Record<string, Loose[]>
    )
      .map(([branch, rows]) => [
        branch,
        (rows ?? []).filter((row) => !belongsToDeselectedCurriculum(row, row?.branch || branch)),
      ])
      .filter(([, rows]) => (rows as Loose[]).length > 0)
  )
  const lessonPlanningData = Object.fromEntries(
    Object.entries((manualData.lessonPlanningData ?? {}) as Record<string, unknown>).filter(
      ([key]) => {
        const parts = key.split('|||')
        if (parts.length >= 3) return selected.has(lower(parts[1]))
        return !removedClasses.some((row) => same(row?.name, parts[parts.length - 1]))
      }
    )
  )
  const removedLessonClassNames = new Set(
    removedClasses
      .filter(
        (removed) =>
          !retainedClasses.some(
            (retained) =>
              same(retained?.name, removed?.name) &&
              (!trim(retained?.branch) ||
                !trim(removed?.branch) ||
                same(retained?.branch, removed?.branch))
          )
      )
      .map((row) => lower(row?.name))
  )
  const retainedStudents = listOf<Loose>(manualData.students).filter(
    (student) =>
      !removedLessonClassNames.has(
        lower(typeof student?.class === 'object' ? student.class?.name : student?.class)
      )
  )
  const branches = listOf<Loose | string>(manualData.branches).map((branch) => {
    if (typeof branch === 'string' || !Array.isArray(branch?.levels)) return branch
    return {
      ...branch,
      levels: branch.levels.filter((level: Loose | string) => {
        const levelName = typeof level === 'string' ? level : level?.name
        const curriculum = curriculumLookup.resolve(
          branch.name,
          levelName,
          typeof level === 'string' ? undefined : level?.curriculum
        )
        if (curriculum) return selected.has(lower(curriculum))
        return (
          belongsToSelectedCurriculum(branch.name, levelName) ||
          !deselectedLevels.some((entry) => same(entry.name, levelName))
        )
      }),
    }
  })
  const deletedLevels = [...listOf<NonNullable<ManualData['deletedLevels']>[number]>(
    manualData.deletedLevels
  )]
  deselectedLevels.forEach((removed) => {
    if (
      deletedLevels.some(
        (entry) =>
          same(entry.curriculum, removed.curriculum) &&
          same(entry.name, removed.name) &&
          same(entry.branch, removed.branch)
      )
    ) {
      return
    }
    deletedLevels.push(removed)
  })

  const result: ManualData & Loose = {
    ...manualData,
    curriculumCodes: Object.fromEntries(
      Object.entries(manualData.curriculumCodes ?? {}).filter(([name]) => selected.has(lower(name)))
    ),
    curriculumShortCodes: Object.fromEntries(
      Object.entries(manualData.curriculumShortCodes ?? {}).filter(([name]) =>
        selected.has(lower(name))
      )
    ),
    levelsByCurriculum,
    branches: branches as ManualData['branches'],
    levels: (manualData.levels ?? []).filter((level) =>
      Object.values(levelsByCurriculum).some((config) =>
        config.levels.some((entry) => same(entry.name, level.name))
      )
    ),
    classes: retainedClasses as ManualData['classes'],
    classesCount: retainedClasses.length,
    subjects: retainedSubjects as ManualData['subjects'],
    subjectsCount: retainedSubjects.length,
    teachers: listOf<Loose>(manualData.teachers).filter((teacher) => {
      const curriculum = curriculumFor(teacher)
      return !curriculum || selected.has(lower(curriculum))
    }) as ManualData['teachers'],
    students: retainedStudents as ManualData['students'],
    studentsCount: retainedStudents.length,
    subjectSelectionsByLevel,
    classesReviewRowsByBranch,
    lessonPlanningData,
    removedLessonClasses: listOf<string>(manualData.removedLessonClasses).filter(
      (name) => !removedLessonClassNames.has(lower(name))
    ),
    deletedClasses: deletedClasses as ManualData['deletedClasses'],
    deletedLevels,
  }
  return result
}

/** Pending deletes of a manualData / classes draft, normalised to arrays. */
export const pendingDeletesOf = (source: Loose | null | undefined) => ({
  deletedClasses: listOf<NonNullable<ManualData['deletedClasses']>[number]>(
    source?.deletedClasses
  ),
  deletedLevels: listOf<NonNullable<ManualData['deletedLevels']>[number]>(source?.deletedLevels),
  deletedBranches: listOf<NonNullable<ManualData['deletedBranches']>[number]>(
    source?.deletedBranches
  ),
  deletedSubjects: listOf<NonNullable<ManualData['deletedSubjects']>[number]>(
    source?.deletedSubjects
  ),
})

// ---------------------------------------------------------------------------------------------
// Branches
// ---------------------------------------------------------------------------------------------

const positiveId = (value: unknown): number | null => {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

const branchNamesOf = (md: Loose): string[] =>
  listOf<unknown>(md?.branches)
    .map((entry: any) => trim(typeof entry === 'string' ? entry : entry?.name))
    .filter(Boolean)

/** Server id of a branch by name (case-insensitive), from `branchIdByName`; null if unsynced. */
export const branchIdOf = (manualData: ManualData | Loose, name: unknown): number | null => {
  const entry = Object.entries(((manualData as Loose)?.branchIdByName ?? {}) as Loose).find(
    ([key]) => lower(key) === lower(name)
  )
  return positiveId(entry?.[1])
}

/**
 * The school's first branch (lowest server id): it holds the General level, and node-api refuses
 * to delete it. A rename is the only change it takes.
 */
export const isFirstBranch = (manualData: ManualData | Loose, name: unknown): boolean => {
  const id = branchIdOf(manualData, name)
  if (!id) return false
  const ids = Object.values(((manualData as Loose)?.branchIdByName ?? {}) as Loose)
    .map(positiveId)
    .filter((value): value is number => value !== null)
  return id === Math.min(...ids)
}

/** First `|||` segment of a lesson-planning class key (`Branch|||Curriculum|||Class`). */
const keyBranch = (key: unknown) => lower(String(key ?? '').split('|||')[0])

/** Branch segment (`@Name`) of a `subjectIdByNameAndClass` key, lower-cased; '' if none. */
const subjectIdKeyBranch = (key: string) => {
  const segment = key.split('|||').find((part) => part.startsWith('@'))
  return segment ? lower(segment.slice(1)) : ''
}

/**
 * manualData without anything that belongs to the `names` branches: their classes, subjects,
 * review rows, per-branch levels, allocation cells and breaks, and the queued class / level /
 * subject deletes of those branches (the branch delete takes all of that with it server-side, so
 * separate deletes would only come back as "not found"). Server ids stay: the branch delete needs
 * its id, and `consumeDeletedBranches` forgets them once it went through.
 */
const pruneBranches = (md: Loose, names: string[]): Loose => {
  const gone = new Set(names.map(lower).filter(Boolean))
  if (gone.size === 0) return md
  const inGone = (branch: unknown) => gone.has(lower(branch))
  const next: Loose = {
    ...md,
    branches: listOf<any>(md.branches).filter(
      (entry) => !inGone(typeof entry === 'string' ? entry : entry?.name)
    ),
  }
  const filterRows = (key: string, field = 'branch') => {
    if (Array.isArray(md[key])) next[key] = md[key].filter((row: Loose) => !inGone(row?.[field]))
  }
  const omitBranchKeys = (source: unknown) =>
    Object.fromEntries(Object.entries((source ?? {}) as Loose).filter(([key]) => !inGone(key)))
  ;['classes', 'subjects', 'break_timings', 'deletedClasses', 'deletedLevels', 'deletedSubjects'].forEach(
    (key) => filterRows(key)
  )
  if (Array.isArray(md.classes)) next.classesCount = next.classes.length
  if (Array.isArray(md.subjects)) next.subjectsCount = next.subjects.length
  if (md.classesReviewRowsByBranch) {
    next.classesReviewRowsByBranch = omitBranchKeys(md.classesReviewRowsByBranch)
  }
  if (md.levelsPerBranch) next.levelsPerBranch = omitBranchKeys(md.levelsPerBranch)
  if (md.levelsByCurriculum) {
    next.levelsByCurriculum = Object.fromEntries(
      Object.entries(md.levelsByCurriculum as Loose).map(([curriculum, cfg]) => [
        curriculum,
        cfg?.levelsPerBranch ? {...cfg, levelsPerBranch: omitBranchKeys(cfg.levelsPerBranch)} : cfg,
      ])
    )
  }
  if (md.lessonPlanningData && typeof md.lessonPlanningData === 'object') {
    next.lessonPlanningData = Object.fromEntries(
      Object.entries(md.lessonPlanningData as Loose).filter(([key]) => !gone.has(keyBranch(key)))
    )
  }
  ;['deletedLessonSlots', 'importedLessonSlots', 'removedLessonClasses'].forEach((key) => {
    if (Array.isArray(md[key])) next[key] = md[key].filter((k: unknown) => !gone.has(keyBranch(k)))
  })
  return next
}

/**
 * manualData after the user removed the `names` branches on Institution Setup: the branches and
 * their rows are gone (`pruneBranches`), and each one the server holds is queued in
 * `deletedBranches` for the branch-delete request. An id another remaining branch still maps to
 * is not queued: when "Main Branch" is renamed by its first real branch, both names point at the
 * same server row.
 */
export const removeBranches = (manualData: ManualData, names: string[]): ManualData => {
  const md = manualData as Loose
  const removed = new Set(names.map(lower))
  const remaining = branchNamesOf(md).filter((name) => !removed.has(lower(name)))
  const liveIds = new Set(remaining.map((name) => branchIdOf(md, name)).filter(Boolean))
  const queue = [...pendingDeletesOf(md).deletedBranches]
  names.forEach((name) => {
    const id = branchIdOf(md, name)
    if (!id || liveIds.has(id) || queue.some((entry) => entry.id === id)) return
    queue.push({name: trim(name), id})
  })
  const result: ManualData & Loose = {
    ...(pruneBranches(md, names) as ManualData),
    deletedBranches: queue,
  }
  return result
}

/**
 * manualData with branch `from` renamed to `to` everywhere the wizard keys rows by branch name.
 * The server id moves to the new name, so the next sync sends the branch as `update` with its id
 * and node-api renames it in place: classes, subjects and teacher allocations stay. (Removing it
 * and adding the new name would delete all of that.)
 */
export const renameBranch = (manualData: ManualData, from: string, to: string): ManualData => {
  const md = manualData as Loose
  const target = trim(to)
  if (!target || same(from, target)) return manualData
  const swap = (branch: unknown) => (same(branch, from) ? target : branch)
  const renameKeys = (source: unknown) =>
    Object.fromEntries(
      Object.entries((source ?? {}) as Loose).map(([key, value]) => [swap(key) as string, value])
    )
  // `Branch|||…` keys (allocation cells and slots); legacy keys without a branch stay.
  const swapKeyBranch = (key: string) => {
    const parts = key.split('|||')
    return parts.length >= 2 && same(parts[0], from) ? [target, ...parts.slice(1)].join('|||') : key
  }
  const next: Loose = {
    ...md,
    branches: listOf<any>(md.branches).map((entry) =>
      typeof entry === 'string' ? swap(entry) : {...entry, name: swap(entry?.name)}
    ),
  }
  ;[
    'classes',
    'subjects',
    'teachers',
    'students',
    'break_timings',
    'deletedClasses',
    'deletedLevels',
    'deletedSubjects',
    'deletedStudents',
  ].forEach((key) => {
    if (!Array.isArray(md[key])) return
    next[key] = md[key].map((row: Loose) =>
      same(row?.branch, from) ? {...row, branch: target} : row
    )
  })
  ;['branchIdByName', 'branchLevelIdMap', 'levelsPerBranch', 'classesReviewRowsByBranch'].forEach(
    (key) => {
      if (md[key]) next[key] = renameKeys(md[key])
    }
  )
  if (md.levelsByCurriculum) {
    next.levelsByCurriculum = Object.fromEntries(
      Object.entries(md.levelsByCurriculum as Loose).map(([curriculum, cfg]) => [
        curriculum,
        cfg?.levelsPerBranch ? {...cfg, levelsPerBranch: renameKeys(cfg.levelsPerBranch)} : cfg,
      ])
    )
  }
  if (md.lessonPlanningData && typeof md.lessonPlanningData === 'object') {
    next.lessonPlanningData = Object.fromEntries(
      Object.entries(md.lessonPlanningData as Loose).map(([key, value]) => [
        swapKeyBranch(key),
        value,
      ])
    )
  }
  ;['deletedLessonSlots', 'importedLessonSlots', 'removedLessonClasses'].forEach((key) => {
    if (Array.isArray(md[key])) next[key] = md[key].map((k: unknown) => swapKeyBranch(String(k)))
  })
  if (md.subjectIdByNameAndClass) {
    next.subjectIdByNameAndClass = Object.fromEntries(
      Object.entries(md.subjectIdByNameAndClass as Loose).map(([key, id]) => [
        key
          .split('|||')
          .map((part) => (part.startsWith('@') && same(part.slice(1), from) ? `@${target}` : part))
          .join('|||'),
        id,
      ])
    )
  }
  return next as ManualData
}

export type BranchDeletePayload = {id: number; name: string; action: 'delete'}

/**
 * Branch rows deleting the queued branches, for their own bulk-import request after the classes
 * one. A branch that is back in the list (re-added under the same name), or whose id a remaining
 * branch maps to, is not deleted.
 */
export const buildBranchDeletePayload = (manualData: ManualData): BranchDeletePayload[] => {
  const md = manualData as Loose
  const live = branchNamesOf(md)
  const liveNames = new Set(live.map(lower))
  const liveIds = new Set(live.map((name) => branchIdOf(md, name)).filter(Boolean))
  const seen = new Set<number>()
  return pendingDeletesOf(md).deletedBranches.flatMap((entry) => {
    const id = positiveId(entry?.id)
    if (!id || seen.has(id) || liveIds.has(id) || liveNames.has(lower(entry?.name))) return []
    seen.add(id)
    return [{id, name: trim(entry.name), action: 'delete' as const}]
  })
}

/**
 * manualData after the branch-delete request succeeded: queue consumed, and the deleted branches'
 * ids forgotten (branch, level and subject ids alike) so a branch added later under the same name
 * starts fresh.
 */
export const consumeDeletedBranches = (manualData: ManualData): ManualData => {
  const md = manualData as Loose
  const {deletedBranches} = pendingDeletesOf(md)
  if (deletedBranches.length === 0) return manualData
  const liveNames = new Set(branchNamesOf(md).map(lower))
  const gone = new Set(
    deletedBranches.map((entry) => lower(entry?.name)).filter((name) => !liveNames.has(name))
  )
  const keep = ([key]: [string, unknown]) => !gone.has(lower(key))
  const result: ManualData & Loose = {
    ...manualData,
    deletedBranches: [],
    branchIdByName: Object.fromEntries(
      Object.entries((md.branchIdByName ?? {}) as Record<string, number>).filter(keep)
    ),
    branchLevelIdMap: Object.fromEntries(Object.entries(md.branchLevelIdMap ?? {}).filter(keep)),
    subjectIdByNameAndClass: Object.fromEntries(
      Object.entries((md.subjectIdByNameAndClass ?? {}) as Record<string, number>).filter(
        ([key]) => !gone.has(subjectIdKeyBranch(key))
      )
    ),
  }
  return result
}

// ---------------------------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------------------------

/**
 * Applies `queue` (the saved draft's pending deletes) onto freshly hydrated manualData and
 * returns the result with every queue carried on it. No-op when the queue is empty.
 */
export const applyPendingDeletes = (
  manualData: ManualData,
  queue: Loose | null | undefined
): ManualData => {
  const pending = pendingDeletesOf(queue)
  const {deletedBranches} = pending
  if (
    pending.deletedClasses.length === 0 &&
    pending.deletedLevels.length === 0 &&
    deletedBranches.length === 0 &&
    pending.deletedSubjects.length === 0
  ) {
    return manualData
  }

  // A removed branch first: everything of it goes, its queued class / level deletes included.
  const goneBranches = new Set(deletedBranches.map((entry) => lower(entry?.name)))
  const md = pruneBranches(
    manualData as Loose,
    deletedBranches.map((entry) => entry?.name)
  )
  const deletedClasses = pending.deletedClasses.filter((c) => !goneBranches.has(lower(c.branch)))
  const deletedLevels = pending.deletedLevels.filter((l) => !goneBranches.has(lower(l.branch)))
  const deletedSubjects = pending.deletedSubjects.filter((s) => !goneBranches.has(lower(s.branch)))

  // A level queued with a branch was removed from that branch only; one without, everywhere.
  const levelMatches = (entry: {curriculum?: string; name: string}, curriculum: unknown, level: unknown) =>
    levelIdentity(entry.curriculum, entry.name) === levelIdentity(curriculum, level) ||
    // Rows may carry no curriculum (legacy) — fall back to the bare level name.
    (!trim(curriculum) && lower(entry.name) === lower(level))
  const levelIsRemoved = (curriculum: unknown, level: unknown, branch?: unknown) =>
    deletedLevels.some(
      (entry) =>
        levelMatches(entry, curriculum, level) &&
        (!trim(entry.branch) || lower(entry.branch) === lower(branch))
    )
  const levelIsRemovedEverywhere = (curriculum: unknown, level: unknown) =>
    deletedLevels.some((entry) => !trim(entry.branch) && levelMatches(entry, curriculum, level))

  // A queued delete names its curriculum (two curricula can share a class name); one queued by
  // an older build has none and matches the class whatever its curriculum.
  const removedClasses = new Set(
    deletedClasses.map((c) => classIdentity(c.name, c.branch, c.level, c.curriculum))
  )
  const isRemovedIdentity = (name: unknown, branch: unknown, level: unknown, curriculum: unknown) =>
    classIdentities(name, branch, level, curriculum).some((identity) =>
      removedClasses.has(identity)
    )
  const classIsRemoved = (row: Loose) =>
    isRemovedIdentity(row?.name, row?.branch, row?.level, row?.curriculum) ||
    levelIsRemoved(row?.curriculum, row?.level, row?.branch)

  // Classes under a removed level go into the class queue too: the classes request must empty
  // the level before the level request can drop it.
  const queuedClasses = [...deletedClasses]
  const queuedIdentities = new Set(removedClasses)
  const classes = ((md.classes ?? []) as Loose[]).filter((row) => {
    if (!classIsRemoved(row)) return true
    const identity = classIdentity(row?.name, row?.branch, row?.level, row?.curriculum)
    const persisted = row?.persisted === true || Number(row?.id) > 0
    if (persisted && !queuedIdentities.has(identity)) {
      queuedIdentities.add(identity)
      queuedClasses.push({
        grade: trim(row?.grade) || trim(row?.name).split('-')[0],
        section: trim(row?.section),
        name: trim(row?.name),
        branch: trim(row?.branch) || undefined,
        level: trim(row?.level),
        curriculum: trim(row?.curriculum) || undefined,
        ...(Number(row?.id) > 0 ? {id: Number(row.id)} : {}),
        ...(row?.persisted === true ? {persisted: true} : {}),
      })
    }
    return false
  })

  // A subject queued for delete (removed from its level) stays hidden until the delete went out.
  const subjectIsRemoved = (row: Loose) =>
    deletedSubjects.some(
      (entry) =>
        lower(entry.name) === lower(row?.name) &&
        lower(entry.class) === lower(row?.class) &&
        (!trim(entry.branch) || !trim(row?.branch) || lower(entry.branch) === lower(row?.branch)) &&
        (!trim(entry.curriculum) ||
          !trim(row?.curriculum) ||
          lower(entry.curriculum) === lower(row?.curriculum))
    )
  const subjects = ((md.subjects ?? []) as Loose[]).filter(
    (row) =>
      !levelIsRemoved(row?.curriculum, row?.level, row?.branch) &&
      !isRemovedIdentity(row?.class, row?.branch, row?.level, row?.curriculum) &&
      !subjectIsRemoved(row)
  )

  // Review rows: drop a removed level's rows; strip removed sections from the rest.
  const reviewRowsByBranch: Record<string, Loose[]> = {}
  Object.entries((md.classesReviewRowsByBranch ?? {}) as Record<string, Loose[]>).forEach(
    ([branch, rows]) => {
      reviewRowsByBranch[branch] = (rows ?? [])
        .filter((row) => !levelIsRemoved(row?.curriculum, row?.level, branch))
        .map((row) => {
          const sections = ((row?.sections ?? []) as unknown[]).filter(
            (section) =>
              !isRemovedIdentity(
                `${trim(row?.gradeName)}-${trim(section)}`,
                branch,
                row?.level,
                row?.curriculum
              )
          )
          return {...row, sections}
        })
        .filter((row) => row.sections.length > 0)
    }
  )

  // Curriculum config: drop removed levels everywhere; drop grades left with no section.
  const levelsByCurriculum: Record<string, Loose> = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Record<string, Loose>).forEach(
    ([curriculum, cfg]) => {
      const levels = ((cfg?.levels ?? []) as Loose[]).filter(
        (level) => !levelIsRemovedEverywhere(curriculum, level?.name)
      )
      const levelsPerBranch: Record<string, string[]> = {}
      Object.entries((cfg?.levelsPerBranch ?? {}) as Record<string, unknown[]>).forEach(
        ([branch, names]) => {
          levelsPerBranch[branch] = (names ?? [])
            .map(String)
            .filter((name) => !levelIsRemoved(curriculum, name, branch))
        }
      )
      const levelDetails: Record<string, Loose> = {}
      Object.entries((cfg?.levelDetails ?? {}) as Record<string, Loose>).forEach(
        ([levelName, detail]) => {
          if (levelIsRemovedEverywhere(curriculum, levelName)) return
          const gradesKept = ((detail?.classes ?? []) as Loose[]).filter((grade) =>
            Object.entries(reviewRowsByBranch).some(([, rows]) =>
              rows.some(
                (row) =>
                  lower(row?.curriculum) === lower(curriculum) &&
                  lower(row?.level) === lower(levelName) &&
                  lower(row?.gradeName) === lower(grade?.name)
              )
            )
          )
          // A level with no review rows at all (e.g. classes never synced) keeps its grades.
          const hasRows = Object.values(reviewRowsByBranch).some((rows) =>
            rows.some(
              (row) =>
                lower(row?.curriculum) === lower(curriculum) &&
                lower(row?.level) === lower(levelName)
            )
          )
          levelDetails[levelName] = {
            ...detail,
            classes: hasRows ? gradesKept : (detail?.classes ?? []),
          }
        }
      )
      levelsByCurriculum[curriculum] = {...cfg, levels, levelsPerBranch, levelDetails}
    }
  )

  const subjectSelectionsByLevel: Record<string, string[]> = {}
  Object.entries((md.subjectSelectionsByLevel ?? {}) as Record<string, string[]>).forEach(
    ([key, value]) => {
      const [curriculum, level] = key.split('|||')
      if (levelIsRemovedEverywhere(curriculum, level)) return
      // A subject removed from this level (queued delete) is not selected any more.
      const removed = new Set(
        deletedSubjects
          .filter(
            (entry) =>
              lower(entry.level) === lower(level) &&
              (!trim(entry.curriculum) || lower(entry.curriculum) === lower(curriculum))
          )
          .map((entry) => lower(entry.name))
      )
      subjectSelectionsByLevel[key] = (value ?? []).filter((name) => !removed.has(lower(name)))
    }
  )

  const levelsCount = Object.values(levelsByCurriculum).reduce(
    (sum, cfg) => sum + ((cfg?.levels ?? []) as unknown[]).length,
    0
  )

  // Spread the TYPED manualData (not the loose alias) so the required fields survive the
  // literal; `classesReviewRowsByBranch` is not on the ManualData type (the wizard reads it
  // via `as any` everywhere) and rides along as an extra key.
  const result: ManualData & Loose = {
    ...(md as ManualData),
    classes: classes as ManualData['classes'],
    classesCount: classes.length,
    subjects: subjects as ManualData['subjects'],
    subjectsCount: subjects.length,
    classesReviewRowsByBranch: reviewRowsByBranch,
    levelsByCurriculum: levelsByCurriculum as ManualData['levelsByCurriculum'],
    levelsCount,
    subjectSelectionsByLevel,
    deletedClasses: queuedClasses,
    deletedLevels,
    deletedBranches,
    deletedSubjects,
  }
  return result
}

export type LevelDeleteBranchPayload = {
  name: string
  id?: number
  action: 'update'
  levels: Array<{name: string; id: number; action: 'delete'} & CurriculumFields>
}

/**
 * Whether another curriculum still has a level called `name` in `branch` — then a bare-name id
 * in `branchLevelIdMap` may be that level's, and must not be used to delete this one.
 */
const sharedWithOtherCurriculum = (
  lookup: ReturnType<typeof buildLevelCurriculumLookup>,
  branch: string,
  name: string,
  curriculum: unknown
) => lookup.curriculaFor(branch, name).some((other) => lower(other) !== lower(curriculum))

/**
 * Branch entries deleting the queued levels, for the follow-up bulk-import request. A level is
 * addressed by the server id `branchLevelIdMap` holds for it on each branch (by curriculum +
 * name, or by bare name while no other curriculum shares it); a level with no id anywhere was
 * never synced and is simply dropped from the queue. Returns [] when nothing is deletable.
 * Every branch entry is `update` — the backend never reads the levels of a branch marked
 * delete. Each level also carries its curriculum: the backend refuses a delete by a name that
 * exists more than once in the branch without it.
 */
export const buildLevelDeletePayload = (manualData: ManualData): LevelDeleteBranchPayload[] => {
  const {deletedLevels} = pendingDeletesOf(manualData)
  if (deletedLevels.length === 0) return []
  const levelIdMap = ((manualData as Loose).branchLevelIdMap ?? {}) as Record<
    string,
    Record<string, number>
  >
  const branchIdByName = (manualData.branchIdByName ?? {}) as Record<string, number>
  const lookup = buildLevelCurriculumLookup(manualData)
  const goneBranches = new Set(
    pendingDeletesOf(manualData).deletedBranches.map((entry) => lower(entry?.name))
  )
  const byBranch = new Map<string, LevelDeleteBranchPayload>()
  deletedLevels.forEach((level) => {
    Object.entries(levelIdMap).forEach(([branchName, levels]) => {
      // A branch being deleted takes its levels with it (its own request, after this one).
      if (goneBranches.has(lower(branchName))) return
      // Removed from one branch only: deleted in that branch only.
      if (trim(level.branch) && lower(level.branch) !== lower(branchName)) return
      const id = lookupLevelId(
        levels,
        level.name,
        level.curriculum,
        sharedWithOtherCurriculum(lookup, branchName, level.name, level.curriculum)
      )
      if (!id) return
      const entry =
        byBranch.get(branchName) ??
        ({
          name: branchName,
          ...(Number(branchIdByName[branchName]) > 0
            ? {id: Number(branchIdByName[branchName])}
            : {}),
          action: 'update',
          levels: [],
        } as LevelDeleteBranchPayload)
      if (!entry.levels.some((l) => l.id === id)) {
        entry.levels.push({
          name: trim(level.name),
          id,
          ...curriculumFieldsFor(level.curriculum, manualData),
          action: 'delete',
        })
      }
      byBranch.set(branchName, entry)
    })
  })
  return Array.from(byBranch.values())
}

/**
 * manualData after the level-delete request succeeded: queue consumed, ids forgotten. A
 * curriculum-qualified entry goes with its level; a bare-name entry only when it held the
 * deleted level's id (with two curricula it may be the surviving level's).
 */
export const consumeDeletedLevels = (manualData: ManualData): ManualData => {
  const {deletedLevels} = pendingDeletesOf(manualData)
  const levelIdMap = ((manualData as Loose).branchLevelIdMap ?? {}) as Record<
    string,
    Record<string, number>
  >
  const lookup = buildLevelCurriculumLookup(manualData)
  const nextMap: Record<string, Record<string, number>> = {}
  Object.entries(levelIdMap).forEach(([branch, levels]) => {
    const branchLevels = deletedLevels.filter(
      (level) => !trim(level.branch) || lower(level.branch) === lower(branch)
    )
    const deletedIds = new Set(
      branchLevels
        .map((level) =>
          lookupLevelId(
            levels,
            level.name,
            level.curriculum,
            sharedWithOtherCurriculum(lookup, branch, level.name, level.curriculum)
          )
        )
        .filter((id): id is number => id !== null)
    )
    const isRemoved = (key: string, id: number) => {
      const parsed = parseLevelIdKey(key)
      return branchLevels.some((level) => {
        if (lower(level.name) !== lower(parsed.name)) return false
        if (parsed.curriculum) return lower(parsed.curriculum) === lower(level.curriculum)
        return deletedIds.has(Number(id)) || !trim(level.curriculum)
      })
    }
    nextMap[branch] = Object.fromEntries(
      Object.entries(levels ?? {}).filter(([key, id]) => !isRemoved(key, id))
    )
  })
  // `branchLevelIdMap` is not on the ManualData type either (read via `as any` by the payload
  // builder and bulkImportIdMerge); same typed spread with an extra key.
  const result: ManualData & Loose = {...manualData, deletedLevels: [], branchLevelIdMap: nextMap}
  return result
}

// ---------------------------------------------------------------------------------------------
// Subjects
// ---------------------------------------------------------------------------------------------

type SubjectDelete = NonNullable<ManualData['deletedSubjects']>[number]


/**
 * `next` — manualData after a level's subject selection changed (Academic Structure, or the
 * Subjects tab) — with every subject REMOVED from a level queued in `deletedSubjects`: one entry
 * per saved (subject, class) of that level, in every branch, by the id on the row or in
 * `subjectIdByNameAndClass`. The rebuilt subject rows only leave such a subject out, which the
 * bulk-import never reads as a removal, so it stayed on the server with its teacher.
 *
 * Only levels present before and after count: a removed level or curriculum deletes its classes,
 * and their subjects go with them. Likewise only classes that still exist. A subject added back
 * before the sync leaves the queue. The subject's allocation cells and the students' links to it
 * are dropped as well — a stale cell with a teacher would re-insert the subject.
 */
export const queueRemovedSubjects = (prev: ManualData, next: ManualData): ManualData => {
  const p = prev as Loose
  const n = next as Loose
  const prevSelections = (p.subjectSelectionsByLevel ?? {}) as Record<string, string[]>
  const nextSelections = (n.subjectSelectionsByLevel ?? {}) as Record<string, string[]>
  const idMap = {...(p.subjectIdByNameAndClass ?? {}), ...(n.subjectIdByNameAndClass ?? {})}
  const nextClasses = listOf<Loose>(n.classes)
  const classStays = (cls: Loose) =>
    nextClasses.some(
      (other) =>
        same(other?.name, cls?.name) &&
        same(other?.branch, cls?.branch) &&
        same(other?.level, cls?.level) &&
        sameOrUnknown(other?.curriculum, cls?.curriculum)
    )
  const selectedNow = (curriculum: unknown, level: unknown, name: unknown) =>
    Object.entries(nextSelections).some(([key, names]) => {
      const [keyCurriculum, keyLevel] = key.split('|||')
      return (
        same(keyLevel, level) &&
        sameOrUnknown(keyCurriculum, curriculum) &&
        (names ?? []).some((selected) => same(selected, name))
      )
    })

  const queued: SubjectDelete[] = []
  const seen = new Set<number>()
  Object.entries(prevSelections).forEach(([key, names]) => {
    if (!(key in nextSelections)) return
    const [curriculum, level] = key.split('|||')
    const kept = new Set((nextSelections[key] ?? []).map(lower))
    const removed = Array.from(new Set((names ?? []).map(trim).filter(Boolean))).filter(
      (name) => !kept.has(lower(name))
    )
    if (removed.length === 0) return
    const levelClasses = listOf<Loose>(p.classes).filter(
      (cls) => same(cls?.level, level) && sameOrUnknown(cls?.curriculum, curriculum) && classStays(cls)
    )
    removed.forEach((name) =>
      levelClasses.forEach((cls) => {
        const classCurriculum = trim(cls?.curriculum) || trim(curriculum)
        const row = listOf<Loose>(p.subjects).find(
          (subject) =>
            same(subject?.name, name) &&
            same(subject?.class, cls?.name) &&
            sameOrUnknown(subject?.branch, cls?.branch) &&
            sameOrUnknown(subject?.curriculum, classCurriculum)
        )
        const id =
          positiveId(row?.id) ?? lookupSubjectId(idMap, name, cls?.name, classCurriculum, cls?.branch)
        if (!id || seen.has(id)) return
        seen.add(id)
        queued.push({
          id,
          name,
          class: trim(cls?.name),
          ...(trim(cls?.branch) ? {branch: trim(cls.branch)} : {}),
          level: trim(level),
          ...(classCurriculum ? {curriculum: classCurriculum} : {}),
        })
      })
    )
  })

  const carried = pendingDeletesOf(p).deletedSubjects.filter(
    (entry) =>
      !seen.has(Number(entry?.id)) &&
      !selectedNow(entry?.curriculum, entry?.level, entry?.name) &&
      nextClasses.some(
        (cls) =>
          same(cls?.name, entry?.class) &&
          sameOrUnknown(cls?.branch, entry?.branch) &&
          sameOrUnknown(cls?.curriculum, entry?.curriculum)
      )
  )
  const deletedSubjects = [...carried, ...queued]
  const result: Loose = {...next, deletedSubjects}
  if (queued.length === 0) return result as ManualData

  const isQueued = (name: unknown, className: unknown, branch?: unknown, curriculum?: unknown) =>
    queued.some(
      (entry) =>
        same(entry.name, name) &&
        same(entry.class, className) &&
        sameOrUnknown(entry.branch, branch) &&
        sameOrUnknown(entry.curriculum, curriculum)
    )
  // Allocation cells: `Branch|||Curriculum|||Class` (legacy `Branch|||Class`) → subject → cell.
  if (n.lessonPlanningData && typeof n.lessonPlanningData === 'object') {
    result.lessonPlanningData = Object.fromEntries(
      Object.entries(n.lessonPlanningData as Record<string, Loose>).map(([classKey, cells]) => {
        const parts = classKey.split('|||')
        const className = parts[parts.length - 1]
        const branch = parts.length >= 2 ? parts[0] : ''
        const curriculum = parts.length >= 3 ? parts[1] : ''
        if (!cells || typeof cells !== 'object') return [classKey, cells]
        return [
          classKey,
          Object.fromEntries(
            Object.entries(cells).filter(
              ([subject]) => !isQueued(subject, className, branch, curriculum)
            )
          ),
        ]
      })
    )
  }
  // Students' subjects: `{name, class}` or the file import's encoded "name|||class".
  if (Array.isArray(n.students)) {
    result.students = (n.students as Loose[]).map((student) => {
      if (!Array.isArray(student?.subjects)) return student
      const subjects = student.subjects.filter((subject: unknown) => {
        const [name, className] =
          typeof subject === 'string'
            ? subject.split('|||')
            : [(subject as Loose)?.name, (subject as Loose)?.class]
        return !isQueued(name, trim(className) || student?.class)
      })
      return subjects.length === student.subjects.length ? student : {...student, subjects}
    })
  }
  return result as ManualData
}

/**
 * manualData after a sync that carried the subject deletes succeeded: queue consumed, and their
 * ids forgotten so the subject added back later goes as a new row instead of an update to the
 * deleted one.
 */
export const consumeDeletedSubjects = (manualData: ManualData): ManualData => {
  const {deletedSubjects} = pendingDeletesOf(manualData)
  if (deletedSubjects.length === 0) return manualData
  const ids = new Set(deletedSubjects.map((entry) => Number(entry?.id)))
  const idMap = ((manualData as Loose).subjectIdByNameAndClass ?? {}) as Record<string, number>
  const result: ManualData & Loose = {
    ...manualData,
    deletedSubjects: [],
    subjectIdByNameAndClass: Object.fromEntries(
      Object.entries(idMap).filter(([, id]) => !ids.has(Number(id)))
    ),
  }
  return result
}

// ---------------------------------------------------------------------------------------------
// Levels removed on Institution Setup
// ---------------------------------------------------------------------------------------------

type LevelDelete = NonNullable<ManualData['deletedLevels']>[number]

const sameLevelDelete = (a: Partial<LevelDelete>, b: Partial<LevelDelete>) =>
  same(a?.name, b?.name) && same(a?.curriculum, b?.curriculum) && same(a?.branch, b?.branch)

/**
 * `queue` (`deletedLevels`) with `level` added once — removed everywhere, or from `level.branch`
 * only. A blank name is ignored.
 */
export const queueLevelDelete = (queue: unknown, level: Partial<LevelDelete>): LevelDelete[] => {
  const list = listOf<LevelDelete>(queue)
  if (!trim(level?.name) || list.some((entry) => sameLevelDelete(entry, level))) return list
  return [
    ...list,
    {
      ...(trim(level.curriculum) ? {curriculum: trim(level.curriculum)} : {}),
      name: trim(level.name),
      ...(trim(level.branch) ? {branch: trim(level.branch)} : {}),
    },
  ]
}

/** `queue` without `level`: it was added back before its delete went out. */
export const unqueueLevelDelete = (queue: unknown, level: Partial<LevelDelete>): LevelDelete[] =>
  listOf<LevelDelete>(queue).filter((entry) => !sameLevelDelete(entry, level))
